import { IS_AWS_ENV } from "constants/app.constants";
import { AwsWellStatus, WellStatus, WellStatusTimeScale } from "constants/charts.enums";
import { createMergeWithDefaults } from "entities/utils";
import { WellStatusOverTimeChartRequestType } from "types";

// Lean request for the Well Status Over Time chart.
// No BASE_CHART_REQUEST: this chart carries none of the wells production payload
// (products, forecast, normalization, decline) — see
// the widget spec. It DOES carry `filterId` and a
// focus grouping, unlike the rig/frac requests, because it is a wells chart.
//
// PROD is the default status, matching the current-status chart's default so a
// chart-type switch does not silently change WHICH population is on screen
// (product). It is also the largest population. The
// ticket's mockups illustrate DUC, but that is a selection, not a default.
export const DEFAULT_WELL_STATUS_OVER_TIME_REQUEST: WellStatusOverTimeChartRequestType = {
  chartType: "WellStatusOverTime",
  status: WellStatus.Prod,
  timeScale: WellStatusTimeScale.Monthly,
  customRange: null,
  focus: { property: "" },
  filterId: null,
  // Off by default; the toolbar's DrainLegsToggle turns it on per chart.
  // wellStatusCurrent.ts keeps its own copy pinned `true` — see the comment
  // there.
  includeDrainLegs: false
};

// AWS defaults to Insights' own: Permit Approved, Weekly. Functions so IS_AWS_ENV is
// never read at import. They also seed NEW charts (via the generator below): the
// factory writes `timeScale` into every entity, so a read-time `?? default` would
// never fire for one. The constant above stays the Azure default.
export const AWS_DEFAULT_OVER_TIME_STATUS: AwsWellStatus = "Permit Approved";

export const defaultWellStatusOverTimeStatus = (): WellStatus | AwsWellStatus =>
  IS_AWS_ENV ? AWS_DEFAULT_OVER_TIME_STATUS : WellStatus.Prod;

export const defaultWellStatusOverTimeRequest = (): WellStatusOverTimeChartRequestType =>
  IS_AWS_ENV
    ? {
        ...DEFAULT_WELL_STATUS_OVER_TIME_REQUEST,
        status: defaultWellStatusOverTimeStatus(),
        timeScale: WellStatusTimeScale.Weekly
      }
    : DEFAULT_WELL_STATUS_OVER_TIME_REQUEST;

// IMPORTANT: `focus` (and especially `focus.seriesColors` / `focus.bin`) must be
// assigned AFTER createEntityState by the controller, never passed as an
// override. createMergeWithDefaults deep-merges, so a narrower new focus passed
// as an override would keep the previous/default object's leftover keys — the
// same trap documented on the rig/frac palette and filter arrays.
export const generateWellStatusOverTimeRequestBody = (
  overrides?: Partial<WellStatusOverTimeChartRequestType>
) => createMergeWithDefaults(defaultWellStatusOverTimeRequest())(overrides);
